import { Mode } from '@/common/entityFramework/enumerations/Mode';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { ObjUtils } from '@/common/utils/ObjUtils';
import { useCallback, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

export const IncDataSrcOutline = (module, { filterId, incDataSrcIds }, isOutline, { entityListRef, navigateTo }) => {
  const rsrc = useRsrc();

  // Flat mode has no children in its rows, so rule counts come from the outline plugin. EntityList builds its
  // colDefs once, so the valueGetter reads through a ref and cells are refreshed once the counts arrive.
  const hasParent = !!filterId || incDataSrcIds?.length > 0;
  const idToRuleCountRef = useRef({});
  useEffect(() => {
    if (!hasParent || isOutline) return;
    rsrc.data(module, 'INC_DATA_SRC_OUTLINE_DATA_PLUGIN', { filterId, incDataSrcIds }).then((rows) => {
      const counts = {};
      (rows ?? []).forEach((row) => {
        if (row.entity === 'Data Source') counts[row.id] = row.incRuleCount;
      });
      idToRuleCountRef.current = counts;
      entityListRef.current?.gridRef?.current?.gridApiRef?.current?.refreshCells({
        columns: ['incRuleCount'],
        force: true,
      });
    });
  }, [rsrc, module, filterId, incDataSrcIds, hasParent, isOutline]);

  const augFieldDefs = useCallback(
    (fieldToFieldDef) => {
      if (!isOutline) {
        if (hasParent) {
          fieldToFieldDef.incRuleCount = {
            colId: 'incRuleCount',
            displayName: 'Rules',
            maxWidth: 110,
            valueGetter: (params) => idToRuleCountRef.current[params.data?.id],
          };
        }
        return;
      }

      ObjUtils.clearObj(fieldToFieldDef);

      fieldToFieldDef.name = { field: 'name', autoGrouping: true };
      fieldToFieldDef.code = {
        colId: 'code',
        valueGetter: (params) => params.data.code,
        displayName: 'Code',
      };
      fieldToFieldDef.entity = { field: 'entity' };
      fieldToFieldDef.children = {
        colId: 'children',
        displayName: 'Rules / Conditions',
        maxWidth: 150,
        valueGetter: (params) => params.data.incRuleCount ?? params.data.incCondCount,
      };
      fieldToFieldDef.trxCol = { displayName: 'Transaction Column', field: 'trxCol' };
      fieldToFieldDef.operator = { field: 'operator', minWidth: 205 };
      fieldToFieldDef.value = {
        field: 'value',
        maxWidth: 205,
        valueGetter: (params) => params.data.value ?? params.data.incListName,
        cellRenderer: (params) => {
          const { data, value } = params;

          if (data.value !== null || !data.incListName) return value;

          return (
            <Link
              component="button"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                navigateTo(module + '/inc-list', {
                  mode: Mode.DETAIL,
                  entityId: data.incListId,
                });
              }}
            >
              {data.incListName}
            </Link>
          );
        },
      };
    },
    [isOutline, hasParent, module, navigateTo],
  );

  const clientSideRowData = useCallback(
    (setDataSource) => {
      rsrc.data(module, 'INC_DATA_SRC_OUTLINE_DATA_PLUGIN', { filterId, incDataSrcIds }).then((res) => {
        setDataSource(res);
      });
    },
    [rsrc, module, filterId, incDataSrcIds],
  );

  const getContextMenuItems = useCallback(
    (params) => {
      if (!isOutline) {
        return entityListRef.current.getContextMenuItems(params);
      }

      const data = params?.node?.data;
      const routeByEntity = {
        'Data Source': module + '/inc-data-src',
        Rule: module + '/inc-rule',
        Condition: module + '/inc-cond',
      };
      return entityListRef.current.getContextMenuItems(params).map((item) => {
        if (item.name !== 'Details' || !data?.id) return item;

        return {
          ...item,
          action: () => {
            navigateTo(routeByEntity[data.entity] ?? module + '/inc-data-src', {
              mode: Mode.DETAIL,
              entityId: data.id,
            });
          },
        };
      });
    },
    [isOutline, entityListRef, navigateTo, module],
  );

  return { augFieldDefs, clientSideRowData, getContextMenuItems };
};
